/**
 * AdminHealthPage — system health for Shubh Time ops team.
 *
 * Panels:
 *  - Calculation latency chart (P50 / P95 / P99) — SVG line chart
 *  - Error rate chart (errors / minute) — SVG bar chart
 *  - Cache hit rate — gauge ring
 *  - Queue depth (RabbitMQ job backlog) — stat card
 *  - Active users (DAU / MAU) — stat cards
 *
 * Auto-refreshes every 30 s when the tab is in focus.
 */
import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminApi, type HealthServiceStatus } from '@/lib/api'
import { cn } from '@/lib/utils'
import { RefreshCw, Activity, Zap, Database, Users, AlertTriangle, CheckCircle2, AlertCircle, XCircle } from 'lucide-react'
import { SkeletonCard, ErrorState } from '@/components/ui'
import dayjs from 'dayjs'

// ── Colour helpers ────────────────────────────────────────────────────────────

function cacheColor(rate: number) {
  if (rate >= 80) return '#22c55e'
  if (rate >= 60) return '#f59e0b'
  return '#ef4444'
}

function queueColor(depth: number) {
  if (depth < 50) return 'text-auspicious'
  if (depth < 200) return 'text-gold'
  return 'text-inauspicious'
}

// ── Mini SVG line chart ───────────────────────────────────────────────────────

interface LineSeries {
  pts: { t: string; v: number }[]
  color: string
  label: string
}

function LineChart({ series, unit: _unit = 'ms' }: { series: LineSeries[]; unit?: string }) {
  const W = 400; const H = 100
  const PAD = { top: 10, right: 10, bottom: 22, left: 36 }
  const allVals = series.flatMap(s => s.pts.map(p => p.v))
  const minV = Math.max(0, Math.min(...allVals) * 0.9)
  const maxV = Math.max(...allVals) * 1.1
  const n = series[0]?.pts.length ?? 1
  const xStep = (W - PAD.left - PAD.right) / (n - 1)
  const yScale = (v: number) =>
    PAD.top + (H - PAD.top - PAD.bottom) * (1 - (v - minV) / (maxV - minV))

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="w-full">
      {/* Grid */}
      {[0.25, 0.5, 0.75].map(f => {
        const y = PAD.top + (H - PAD.top - PAD.bottom) * f
        const label = Math.round(minV + (maxV - minV) * (1 - f))
        return (
          <g key={f}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y}
              style={{ stroke: 'var(--chart-border)' }} strokeWidth={1} />
            <text x={PAD.left - 4} y={y} textAnchor="end" style={{ fill: 'var(--chart-text-dim)' }}
              fontSize={8} dominantBaseline="middle" fontFamily="Geist Mono, monospace">
              {label}
            </text>
          </g>
        )
      })}
      {/* Series */}
      {series.map(s => {
        const pts = s.pts.map((p, i) => ({ x: PAD.left + i * xStep, y: yScale(p.v) }))
        const polyline = pts.map(p => `${p.x},${p.y}`).join(' ')
        return (
          <polyline key={s.label} points={polyline} fill="none"
            stroke={s.color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        )
      })}
      {/* X labels (first, mid, last) */}
      {[0, Math.floor(n / 2), n - 1].map(i => {
        const p = series[0]?.pts[i]
        if (!p) return null
        return (
          <text key={i} x={PAD.left + i * xStep} y={H - 4}
            textAnchor="middle" style={{ fill: 'var(--chart-text-dim)' }} fontSize={8}
            fontFamily="Geist Mono, monospace">
            {p.t}
          </text>
        )
      })}
    </svg>
  )
}

// ── Bar chart ─────────────────────────────────────────────────────────────────

function BarChart({ pts, color }: { pts: { t: string; v: number }[]; color: string }) {
  const W = 400; const H = 80
  const PAD = { top: 8, right: 10, bottom: 22, left: 28 }
  const maxV = Math.max(...pts.map(p => p.v), 1)
  const barW = (W - PAD.left - PAD.right) / pts.length - 2
  const chartH = H - PAD.top - PAD.bottom

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="w-full">
      {[0.5, 1].map(f => {
        const y = PAD.top + chartH * (1 - f)
        return (
          <line key={f} x1={PAD.left} x2={W - PAD.right} y1={y} y2={y}
            style={{ stroke: 'var(--chart-border)' }} strokeWidth={1} />
        )
      })}
      {pts.map((p, i) => {
        const bh = Math.max(2, (p.v / maxV) * chartH)
        const x = PAD.left + i * ((W - PAD.left - PAD.right) / pts.length) + 1
        const y = PAD.top + chartH - bh
        return (
          <rect key={i} x={x} y={y} width={barW} height={bh}
            fill={color} fillOpacity={0.7} rx={1} />
        )
      })}
      {[0, Math.floor(pts.length / 2), pts.length - 1].map(i => {
        const p = pts[i]
        if (!p) return null
        const x = PAD.left + i * ((W - PAD.left - PAD.right) / pts.length) + barW / 2
        return (
          <text key={i} x={x} y={H - 4} textAnchor="middle" style={{ fill: 'var(--chart-text-dim)' }}
            fontSize={8} fontFamily="Geist Mono, monospace">{p.t}</text>
        )
      })}
    </svg>
  )
}

// ── Cache ring ────────────────────────────────────────────────────────────────

function CacheGauge({ rate }: { rate: number }) {
  const size = 80; const sw = 8
  const r = (size - sw * 2) / 2
  const circ = 2 * Math.PI * r
  const arc = circ * 0.75
  const fill = arc * (rate / 100)
  const color = cacheColor(rate)

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: 'var(--chart-border)' }} strokeWidth={sw}
          strokeDasharray={`${arc} ${circ - arc}`} strokeLinecap="round"
          transform={`rotate(135 ${size / 2} ${size / 2})`} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
          transform={`rotate(135 ${size / 2} ${size / 2})`}
          style={{ filter: `drop-shadow(0 0 4px ${color}60)` }} />
        <text x={size / 2} y={size / 2 + 4} textAnchor="middle"
          fill={color} fontSize={14} fontWeight={700} fontFamily="Geist Mono, monospace">
          {rate}%
        </text>
      </svg>
      <span className="text-2xs text-star-dim font-mono">Hit Rate</span>
    </div>
  )
}

// ── Service Status ────────────────────────────────────────────────────────────

const statusConfig = {
  ok:       { icon: CheckCircle2,  color: 'text-auspicious',  bg: 'bg-auspicious/10',  border: 'border-auspicious/20',  label: 'OK'       },
  degraded: { icon: AlertCircle,   color: 'text-gold',        bg: 'bg-gold/10',         border: 'border-gold/20',        label: 'Degraded' },
  down:     { icon: XCircle,       color: 'text-inauspicious',bg: 'bg-inauspicious/10', border: 'border-inauspicious/20',label: 'Down'     },
} as const

function ServiceRow({ service }: { service: HealthServiceStatus }) {
  const cfg = statusConfig[service.status]
  const Icon = cfg.icon
  return (
    <div className={cn(
      'flex items-center justify-between px-3 py-2 rounded-lg border',
      cfg.bg, cfg.border,
    )}>
      <div className="flex items-center gap-2">
        <Icon size={13} className={cfg.color} />
        <span className="text-xs text-star-bright font-medium">{service.name}</span>
        {service.message && (
          <span className="text-2xs text-star-dim">— {service.message}</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        {service.latencyMs !== undefined && (
          <span className="text-2xs font-mono text-star-dim">{service.latencyMs} ms</span>
        )}
        <span className={cn('text-2xs font-medium font-mono', cfg.color)}>{cfg.label}</span>
      </div>
    </div>
  )
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon, color = 'text-star-bright' }: {
  label: string; value: string | number; sub?: string
  icon: React.ReactNode; color?: string
}) {
  return (
    <div className="card-cosmic p-4 flex items-start gap-3">
      <div className="mt-0.5 text-gold-dim">{icon}</div>
      <div>
        <p className="text-2xs font-mono uppercase tracking-wider text-star-dim mb-0.5">{label}</p>
        <p className={cn('text-xl font-bold font-mono', color)}>{value}</p>
        {sub && <p className="text-2xs text-star-dim mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AdminHealthPage() {
  const [lastRefresh, setLastRefresh] = useState(new Date())

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: () => adminApi.getHealth(),
    retry: 1,
    refetchInterval: 30_000,
  })

  const handleRefresh = useCallback(() => {
    refetch()
    setLastRefresh(new Date())
  }, [refetch])

  // Auto-refresh on visibility change
  useEffect(() => {
    const handler = () => { if (!document.hidden) handleRefresh() }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [handleRefresh])

  if (isLoading && !data) return (
    <div className="p-6 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} className="h-24" />)}
      </div>
      <SkeletonCard className="h-40" />
      <SkeletonCard className="h-40" />
    </div>
  )
  if ((isError || !data) && !data) return <ErrorState message="Unable to load health data." onRetry={refetch} />

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-star-bright">System Health</h2>
          <p className="text-xs text-star-muted">
            Live metrics — auto-refreshes every 30s
            <span className="ml-2 font-mono text-2xs text-star-dim">
              · last updated {dayjs(lastRefresh).format('HH:mm:ss')}
            </span>
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className={cn('p-2 rounded-lg border border-cosmic-mist text-star-dim hover:text-star-bright hover:border-violet-dark transition-colors', isLoading && 'animate-spin')}
          aria-label="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {(isError || !data) && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold/5 border border-gold/20">
          <span className="text-gold text-xs">✦</span>
          <span className="text-xs text-star-muted">Showing sample data — connect to metrics API for live system health.</span>
        </div>
      )}

      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="DAU" value={data.dau.toLocaleString()} icon={<Users size={16} />}
          sub="Daily active users" color="text-gold-dim" />
        <StatCard label="MAU" value={data.mau.toLocaleString()} icon={<Users size={16} />}
          sub="Monthly active users" />
        <StatCard label="Queue Depth" value={data.queueDepth} icon={<Database size={16} />}
          sub="Jobs in backlog" color={queueColor(data.queueDepth)} />
        <StatCard label="Uptime" value={`${data.uptime}%`} icon={<Zap size={16} />}
          sub="30-day rolling" color="text-auspicious" />
      </div>

      {/* Latency chart */}
      <div className="card-cosmic p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-gold-dim" />
            <p className="text-xs font-medium text-star-bright">Calculation Latency</p>
          </div>
          <div className="flex items-center gap-4">
            {[
              { label: 'P50', color: '#22c55e' },
              { label: 'P95', color: '#f59e0b' },
              { label: 'P99', color: '#ef4444' },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 rounded" style={{ background: l.color }} />
                <span className="text-2xs font-mono text-star-dim">{l.label}</span>
              </div>
            ))}
          </div>
        </div>
        <LineChart
          unit="ms"
          series={[
            { pts: data.latency.p50, color: '#22c55e', label: 'P50' },
            { pts: data.latency.p95, color: '#f59e0b', label: 'P95' },
            { pts: data.latency.p99, color: '#ef4444', label: 'P99' },
          ]}
        />
        <div className="flex gap-4 text-2xs font-mono text-star-dim">
          {(['p50', 'p95', 'p99'] as const).map(k => {
            const last = data.latency[k].at(-1)?.v ?? 0
            return <span key={k}>{k.toUpperCase()}: {last} ms</span>
          })}
        </div>
      </div>

      {/* API service status */}
      {(() => {
        const p99Last = data.latency.p99.at(-1)?.v ?? 0
        const errLast = data.errorRate.at(-1)?.v ?? 0
        const services: HealthServiceStatus[] = data.services ?? [
          { name: 'API Server',   status: errLast > 10 ? 'degraded' : 'ok',      latencyMs: data.latency.p50.at(-1)?.v },
          { name: 'Calculation Engine', status: p99Last > 2000 ? 'degraded' : 'ok', latencyMs: p99Last },
          { name: 'Redis Cache',  status: data.cacheHitRate < 60 ? 'degraded' : 'ok' },
          { name: 'Job Queue',    status: data.queueDepth > 200 ? 'degraded' : data.queueDepth > 500 ? 'down' : 'ok', message: data.queueDepth > 200 ? `${data.queueDepth} jobs pending` : undefined },
        ]
        return (
          <div className="card-cosmic p-4 space-y-3">
            <p className="text-xs font-medium text-star-bright">API Services</p>
            <div className="space-y-2">
              {services.map(s => <ServiceRow key={s.name} service={s} />)}
            </div>
          </div>
        )
      })()}

      {/* Error rate + cache row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Error rate */}
        <div className="sm:col-span-2 card-cosmic p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-inauspicious" />
            <p className="text-xs font-medium text-star-bright">Error Rate</p>
            <span className="ml-auto text-2xs font-mono text-star-dim">
              {(data.errorRate.at(-1)?.v ?? 0)} err/min
            </span>
          </div>
          <BarChart pts={data.errorRate} color="#ef4444" />
        </div>

        {/* Cache hit rate */}
        <div className="card-cosmic p-4 flex flex-col items-center justify-center gap-3">
          <p className="text-xs font-medium text-star-bright self-start">Redis Cache</p>
          <CacheGauge rate={data.cacheHitRate} />
          <p className="text-2xs text-star-dim text-center">
            {data.cacheHitRate >= 80
              ? 'Cache performing well'
              : data.cacheHitRate >= 60
                ? 'Cache below target'
                : 'Cache needs attention'}
          </p>
        </div>
      </div>
    </div>
  )
}
